import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Tooltip, Select, Row, Col, Alert, Space, message } from "antd";
import { CalculatorOutlined } from "@ant-design/icons";
import { ModalItem, InputItem, SelectItem, DatePickerItem, ButtonItem } from "../../../Components/Items";
import {
  calcularCurp, mismaCurpBase, claveCurpDeEstado, estadoDeClaveCurp,
  ENTIDADES_CURP, SEGMENTOS_CURP, SEXO_CURP, URL_CONSULTA_CURP,
} from "./curp";

// Calculadora de CURP: va como `suffix` del InputItem de la CURP en Agregar Empleado, Editar
// Empleado y el paso 1 del alta por pasos (así el FormItem conserva el campo como hijo directo).
// Abre con los datos del empleado que ya están en el formulario; ahí se pueden corregir, y "Usar
// esta CURP" devuelve al formulario la CURP y los datos con que se calculó.
//
// Props:
//   obtenerForm()          el formRef.current del Form de la casa.
//   datosExtra             sexo / fechaNacimiento / estadoNacimientoId cuando el formulario no los
//                          tiene (paso 1 del alta por pasos). Mandan sobre lo del formulario.
//   nacimientoEnFormulario false para no escribir sexo, fecha y estado en el formulario (porque no
//                          tiene esos campos) y sólo entregarlos en onUsar.
//   onUsar(datos)          { curp, sexo, fechaNacimiento, estadoNacimientoId }.
//
// Los campos del modal son controlados, sin Form anidado: el modal se monta dentro del Form padre
// (por el portal sólo cambia el DOM) y así no se mezcla con él ni con su candado de 5 s.

const { Option } = Select;
const CAMPOS_NOMBRE = ["nombre", "apellidoPaterno", "apellidoMaterno"];
const VACIO = { nombre: "", apellidoPaterno: "", apellidoMaterno: "", fechaNacimiento: null, sexo: undefined, entidad: undefined };

// El catálogo de estados se pide una sola vez, la primera vez que se abre una calculadora.
let estadosCache = null;
const cargarEstados = async (headersToken) => {
  if (estadosCache) return estadosCache;
  const res = await axios.post("estados/selectEstados", null, { headers: headersToken });
  if (!Array.isArray(res.data)) return [];
  estadosCache = res.data;
  return estadosCache;
};

// Campo y ClaveSeparada también los usa la calculadora de RFC (CalculadoraRfc.js).
export const Campo = ({ etiqueta, children, ayuda }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>{etiqueta}</div>
    {children}
    {ayuda && <div style={{ fontSize: 11, color: "#d48806", marginTop: 2 }}>{ayuda}</div>}
  </div>
);

export const ClaveSeparada = ({ clave, segmentos }) => (
  <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
    {segmentos.map((s) => (
      <div key={s.desde} style={{ textAlign: "center" }}>
        <div style={{
          fontFamily: "monospace", fontSize: 24, fontWeight: 600, letterSpacing: 2,
          borderBottom: "3px solid #00BFD6", padding: "0 2px",
        }}>
          {clave.slice(s.desde, s.hasta)}
        </div>
        <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>{s.titulo}</div>
      </div>
    ))}
  </div>
);

const CalculadoraCurp = ({ obtenerForm, datosExtra, nacimientoEnFormulario = true, onUsar }) => {
  const headersToken = useSelector((state) => state.user.headersToken);
  const [visible, setVisible] = useState(false);
  const [datos, setDatos] = useState(VACIO);
  const [curpCapturada, setCurpCapturada] = useState("");
  const [estados, setEstados] = useState([]);
  const [estadoActual, setEstadoActual] = useState(null);
  const [estadoActualId, setEstadoActualId] = useState(null);

  const cambiar = (campo) => (valor) => setDatos((d) => ({ ...d, [campo]: valor }));
  const cambiarTexto = (campo) => (e) => setDatos((d) => ({ ...d, [campo]: e.target.value }));

  const abrir = async () => {
    const form = obtenerForm?.();
    const extra = datosExtra || {};
    const leer = (campo) => (extra[campo] != null ? extra[campo] : form?.getFieldValue(campo));
    const sexo = leer("sexo");
    const estadoNacimientoId = leer("estadoNacimientoId") || null;

    setDatos({
      nombre: form?.getFieldValue("nombre") || "",
      apellidoPaterno: form?.getFieldValue("apellidoPaterno") || "",
      apellidoMaterno: form?.getFieldValue("apellidoMaterno") || "",
      fechaNacimiento: leer("fechaNacimiento") || null,
      sexo: SEXO_CURP[sexo] ? sexo : undefined,
      entidad: undefined,
    });
    setCurpCapturada(String(form?.getFieldValue("curp") || "").trim().toUpperCase());
    setEstadoActualId(estadoNacimientoId);
    setEstadoActual(null);
    setVisible(true);

    try {
      const lista = await cargarEstados(headersToken);
      setEstados(lista);
      const estado = estadoNacimientoId ? lista.find((e) => String(e._id) === String(estadoNacimientoId)) : null;
      setEstadoActual(estado || null);
      const clave = claveCurpDeEstado(estado);
      // Si mientras cargaba ya eligieron una entidad, se respeta.
      if (clave) setDatos((d) => ({ ...d, entidad: d.entidad || clave }));
    } catch (err) {
      console.log(err);
      message.error("No se pudo cargar el catálogo de estados");
    }
  };

  const { curp, faltan } = calcularCurp(datos);
  // Si la CURP capturada es de los mismos datos se conserva: trae la homoclave real de RENAPO.
  const coincide = !!curp && mismaCurpBase(curp, curpCapturada);
  const curpFinal = coincide ? curpCapturada : curp;
  const estadoNoReconocido = estadoActual && !claveCurpDeEstado(estadoActual) ? estadoActual.nombre : null;

  const usar = () => {
    if (!curpFinal) {
      message.warning(`Falta: ${faltan.join(", ")}`);
      return;
    }
    // El estado de nacimiento sólo se cambia si la entidad elegida es otra que la del empleado.
    const estadoNacimientoId = datos.entidad === claveCurpDeEstado(estadoActual)
      ? estadoActualId
      : estadoDeClaveCurp(datos.entidad, estados)?._id;
    const nacimiento = {
      sexo: datos.sexo,
      fechaNacimiento: datos.fechaNacimiento,
      ...(estadoNacimientoId ? { estadoNacimientoId } : {}),
    };

    const form = obtenerForm?.();
    const nombres = {};
    for (const campo of CAMPOS_NOMBRE) {
      if (String(datos[campo] || "").trim() !== String(form?.getFieldValue(campo) || "").trim()) nombres[campo] = datos[campo];
    }
    form?.setFieldsValue({ curp: curpFinal, ...nombres, ...(nacimientoEnFormulario ? nacimiento : {}) });
    // setFieldsValue no revalida: sin esto se quedaría a la vista un "La CURP no es válida" viejo.
    Promise.resolve(form?.validateFields(["curp", ...Object.keys(nombres)])).catch(() => {});
    onUsar?.({ curp: curpFinal, ...nacimiento });
    setVisible(false);
  };

  return (
    <>
      <Tooltip title="Calcular CURP">
        <CalculatorOutlined onClick={abrir} style={{ color: "#00BFD6", cursor: "pointer" }} />
      </Tooltip>
      <ModalItem
        title="Calculadora de CURP"
        visible={visible}
        onCancel={() => setVisible(false)}
        width={640}
        destroyOnClose
        paddingTop
      >
        <Row gutter={16}>
          <Col span={12}>
            <Campo etiqueta="Nombre(s)">
              <InputItem value={datos.nombre} onChange={cambiarTexto("nombre")} placeholder="Nombre(s)" />
            </Campo>
            <Campo etiqueta="Apellido paterno">
              <InputItem value={datos.apellidoPaterno} onChange={cambiarTexto("apellidoPaterno")} placeholder="Apellido paterno" />
            </Campo>
            <Campo etiqueta="Apellido materno">
              <InputItem value={datos.apellidoMaterno} onChange={cambiarTexto("apellidoMaterno")} placeholder="Sin apellido materno" />
            </Campo>
          </Col>
          <Col span={12}>
            <Campo etiqueta="Fecha de nacimiento">
              <DatePickerItem value={datos.fechaNacimiento} onChange={cambiar("fechaNacimiento")} format="DD-MM-YYYY"
                placeholder="Fecha de nacimiento" style={{ width: "100%" }} />
            </Campo>
            <Campo etiqueta="Sexo">
              <SelectItem value={datos.sexo} onChange={cambiar("sexo")} placeholder="Sexo" noFocusSiguiente>
                <Option value="Hombre">Hombre</Option>
                <Option value="Mujer">Mujer</Option>
              </SelectItem>
            </Campo>
            <Campo etiqueta="Entidad de nacimiento"
              ayuda={estadoNoReconocido && !datos.entidad ? `"${estadoNoReconocido}" no es una entidad de la CURP: elige una, o "Nacido en el extranjero".` : null}>
              <SelectItem value={datos.entidad} onChange={cambiar("entidad")} placeholder="Entidad de nacimiento" noFocusSiguiente>
                {ENTIDADES_CURP.map((e) => (
                  <Option key={e.clave} value={e.clave}>{`${e.clave} - ${e.nombre}`}</Option>
                ))}
              </SelectItem>
            </Campo>
          </Col>
        </Row>

        <div style={{
          background: "#f4fcfd", border: "1px solid #b8eaf1", borderRadius: 8,
          padding: "14px 16px", margin: "4px 0 12px", textAlign: "center",
        }}>
          {curpFinal
            ? <ClaveSeparada clave={curpFinal} segmentos={SEGMENTOS_CURP} />
            : <span style={{ color: "#999" }}>Para calcularla falta: {faltan.join(", ")}</span>}
        </div>

        {coincide && (
          <Alert type="success" showIcon style={{ marginBottom: 10 }}
            message="Coincide con la CURP capturada; se conserva su homoclave." />
        )}
        {!coincide && curp && curpCapturada && (
          <Alert type="warning" showIcon style={{ marginBottom: 10 }}
            message={`La CURP capturada (${curpCapturada}) no corresponde a estos datos.`} />
        )}
        {!coincide && curp && (
          <Alert type="info" showIcon style={{ marginBottom: 10 }}
            message="Los dos últimos caracteres los asigna RENAPO"
            description={
              <>
                Se propone {curp[16] === "A" ? "\"A\" (nacidos desde 2000)" : "\"0\" (nacidos antes de 2000)"} y el dígito
                verificador que le corresponde. Confírmala en{" "}
                <a href={URL_CONSULTA_CURP} target="_blank" rel="noopener noreferrer">gob.mx/curp</a>.
              </>
            } />
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 0 10px" }}>
          <Space>
            <ButtonItem danger onClick={() => setVisible(false)}>Cancelar</ButtonItem>
            <ButtonItem type="primary" disabled={!curpFinal} onClick={usar}>Usar esta CURP</ButtonItem>
          </Space>
        </div>
      </ModalItem>
    </>
  );
};

export default CalculadoraCurp;
